/**
 * Position Recommendation Rules Configuration
 * 
 * This file imports sport-specific position recommendation rules and creates the
 * configuration for recommending positions to players based on their attributes.
 * 
 * Each sport's rules are defined in separate files (e.g., baseballPositions.ts)
 * to keep the code organized and maintainable.
 */

import type { Sport } from '../types';
import { BASEBALL_POSITION_RULES } from './positions/baseballPositions';
import { FOOTBALL_POSITION_RULES } from './positions/footballPositions';
import { BASKETBALL_POSITION_RULES } from './positions/basketballPositions';
import { SOCCER_POSITION_RULES } from './positions/soccerPositions';
import { VOLLEYBALL_POSITION_RULES } from './positions/volleyballPositions';
import { HOCKEY_POSITION_RULES } from './positions/hockeyPositions';
import { LACROSSE_POSITION_RULES } from './positions/lacrossePositions';
import { TENNIS_POSITION_RULES } from './positions/tennisPositions';

/**
 * Attribute requirement for a position
 */
export interface AttributeRequirement {
  attribute: string;
  minValue: number;
  required?: boolean; // If true, ALL requirements must be met. If false, ANY requirement can be met.
}

/**
 * Position recommendation rule
 */
export interface PositionRule {
  position: string;
  tier: 'premium' | 'standard' | 'easier'; // Difficulty tier
  requirements: AttributeRequirement[]; // Minimum thresholds that must be met
  scoreFormula: (attributes: Map<string, number>) => number; // Scoring function
  description?: string; // Human-readable description
}

/**
 * Sport-specific position recommendation configuration
 */
export interface PositionRecommendationConfig {
  sport: Sport;
  positions: PositionRule[];
  maxRecommendations?: number; // Default: 4
}

/**
 * Sport-specific position recommendation configurations
 * Each sport imports its rules from a separate file for better organization
 */
export const POSITION_RECOMMENDATION_CONFIGS: Record<Sport, PositionRecommendationConfig> = {
  Baseball: {
    sport: 'Baseball',
    positions: BASEBALL_POSITION_RULES,
    maxRecommendations: 4,
  },
  Softball: {
    sport: 'Softball',
    positions: BASEBALL_POSITION_RULES, // Same rules as baseball
    maxRecommendations: 4,
  },
  Football: {
    sport: 'Football',
    positions: FOOTBALL_POSITION_RULES,
    maxRecommendations: 5,
  },
  FlagFootball: {
    sport: 'FlagFootball',
    positions: FOOTBALL_POSITION_RULES, // Similar to football
    maxRecommendations: 5,
  },
  Basketball: {
    sport: 'Basketball',
    positions: BASKETBALL_POSITION_RULES,
    maxRecommendations: 3,
  },
  Soccer: {
    sport: 'Soccer',
    positions: SOCCER_POSITION_RULES,
    maxRecommendations: 3,
  },
  Volleyball: {
    sport: 'Volleyball',
    positions: VOLLEYBALL_POSITION_RULES,
    maxRecommendations: 3,
  },
  Hockey: {
    sport: 'Hockey',
    positions: HOCKEY_POSITION_RULES,
    maxRecommendations: 3,
  },
  Lacrosse: {
    sport: 'Lacrosse',
    positions: LACROSSE_POSITION_RULES,
    maxRecommendations: 3,
  },
  Tennis: {
    sport: 'Tennis',
    positions: TENNIS_POSITION_RULES,
    maxRecommendations: 3,
  },
};

/**
 * Check if a player meets the requirements for a position
 */
export function meetsPositionRequirements(
  requirements: AttributeRequirement[],
  attributes: Map<string, number>
): boolean {
  if (requirements.length === 0) return true;

  // Separate required and optional requirements
  const requiredReqs = requirements.filter(r => r.required !== false);
  const optionalReqs = requirements.filter(r => r.required === false);

  // All required requirements must be met
  const allRequiredMet = requiredReqs.every(req => {
    const value = attributes.get(req.attribute) || 0;
    return value >= req.minValue;
  });

  if (!allRequiredMet) return false;

  // If there are optional requirements, at least one must be met
  if (optionalReqs.length > 0) {
    const anyOptionalMet = optionalReqs.some(req => {
      const value = attributes.get(req.attribute) || 0;
      return value >= req.minValue;
    });
    return anyOptionalMet;
  }

  return true;
}

/**
 * Get position recommendations for a player based on their attributes
 */
export function getPositionRecommendations(
  attributes: Array<{ attribute_key: string; value: number }>,
  sport: Sport
): string[] {
  const config = POSITION_RECOMMENDATION_CONFIGS[sport];
  if (!config || config.positions.length === 0) {
    return [];
  }

  // Convert attributes to Map for easy lookup
  const attrMap = new Map<string, number>();
  attributes.forEach(attr => {
    attrMap.set(attr.attribute_key, attr.value);
  });

  // Calculate scores for all positions that meet requirements
  const positionScores: Array<{ position: string; score: number }> = [];

  for (const rule of config.positions) {
    if (meetsPositionRequirements(rule.requirements, attrMap)) {
      const score = rule.scoreFormula(attrMap);
      positionScores.push({ position: rule.position, score });
    }
  }

  // Sort by score (highest first)
  positionScores.sort((a, b) => b.score - a.score);

  // Return top N positions
  const maxRecs = config.maxRecommendations || 4;
  return positionScores
    .slice(0, maxRecs)
    .map(p => p.position);
}
